/* eslint-disable no-nested-ternary */
import moment from 'moment-timezone';
import { PSO } from '@mummssoftware/utils/constants';
import {
  getUserCredentials,
  getLocalOrderTime,
} from '@mummssoftware/utils/orders/orders-utils';
import { formatDateTimeMMDD } from '@mummssoftware/utils/formatters';
import { dateToMoment, momentToDate } from '@mummssoftware/utils/dates';
import {
  RTN,
  CC,
  ADM,
  DIS,
  MMD,
  OTH,
  FOR_CAUSE,
  LEFT_GEOGRAPHIC_AREA,
  NON_CERTIFIED,
  DEATH,
  REVOKED,
  TRANSFERRED,
} from '@mummssoftware/utils/orders/orders-constants';
import { orderTypeMapping } from '@mummssoftware/utils/orders/orders-maps';
import { DischargeTypes } from '../Context/constants';
import defaults from '../../translations/en.json';

export const isDisType = (
  orderType: mumms.EntityOrderType | mumms.DischargeEntityType | null,
) => orderType && orderTypeMapping[orderType] === DIS;
export const isADMType = (
  orderType: mumms.EntityOrderType | mumms.DischargeEntityType | null,
) => orderType === ADM;
export const isPSOType = (
  orderType: mumms.EntityOrderType | mumms.DischargeEntityType | null,
) => orderType === PSO;

/**
 * Discharge statement
 */
export const getFormattedDisMsg = (
  intl: any,
  user: mumms.User,
  ordertype: mumms.DischargeEntityType | null,
  isorder: boolean,
  isPhysicianRoleGroup: boolean,
  date: number | Date | null,
  dischargeReason?: mumms.OptionType | undefined,
  hospiceText?: string | undefined,
) => {
  const dateText = formatDateTimeMMDD(date);
  const userCredentials = getUserCredentials(user);
  const signDate = getLocalOrderTime(user);

  switch (ordertype) {
    case FOR_CAUSE: {
      const text = isorder
        ? isPhysicianRoleGroup
          ? defaults.forCausePhysOrder
          : defaults.forCauseOrder
        : defaults.forCauseNote;
      return intl.formatMessage(text, {
        date: dateText,
        dischargeReason: dischargeReason?.label,
        userCredentials,
        signDate,
      });
    }
    case LEFT_GEOGRAPHIC_AREA: {
      const text = isorder
        ? isPhysicianRoleGroup
          ? defaults.geographicPhysOrder
          : defaults.geographicOrder
        : defaults.geographicNote;

      return intl.formatMessage(text, {
        date: dateText,
        userCredentials,
        signDate,
      });
    }
    case NON_CERTIFIED: {
      const text = isorder
        ? isPhysicianRoleGroup
          ? defaults.nonCertifiedPhysOrder
          : defaults.nonCertifiedOrder
        : defaults.nonCertifiedNote;

      return intl.formatMessage(text, {
        date: dateText,
        userCredentials,
        signDate,
      });
    }
    case DEATH: {
      const text = isorder ? defaults.deathOrder : defaults.deathNote;

      return intl.formatMessage(text, {
        date: dateText,
        userCredentials,
        signDate,
      });
    }
    case REVOKED: {
      const text = isorder ? defaults.revokedOrder : defaults.revokedNote;

      return intl.formatMessage(text, {
        date: dateText,
        dischargeReason: dischargeReason?.label,
        userCredentials,
        signDate,
      });
    }
    case TRANSFERRED: {
      const text = isorder ? defaults.transferOrder : defaults.transferNote;

      return intl.formatMessage(text, {
        date: dateText,
        hospice: hospiceText,
        userCredentials,
        signDate,
      });
    }
    default:
      return intl.formatMessage(defaults.dischargeOrder, {
        date: dateText,
        dischargeReason: dischargeReason?.label,
        userCredentials,
        signDate,
      });
  }
};

/**
 * Discharge reason
 */
export const getDischargeReason = (
  ordertype: mumms.DischargeEntityType | null,
  patient: mumms.patient,
  dischargeReason: mumms.OptionType | undefined,
  dischargeTypes: DischargeTypes | null,
) => {
  switch (ordertype) {
    case REVOKED:
    case FOR_CAUSE:
      return dischargeReason?.label;
    case NON_CERTIFIED:
    case LEFT_GEOGRAPHIC_AREA: {
      const reasons = (dischargeTypes as any)[ordertype];
      return reasons.length && reasons[0].label;
    }
    case DEATH: {
      const { currentFacilityType } = patient;
      const isHome = currentFacilityType === 'Home';
      const reasons = (dischargeTypes as any)[ordertype];
      return reasons.length && isHome ? reasons[1].label : reasons[0].label;
    }
    case TRANSFERRED: {
      const { loc } = patient;
      const isRTN = loc === RTN || loc === CC;
      const reasons = (dischargeTypes as any)[ordertype];
      return reasons.length && isRTN ? reasons[1].label : reasons[0].label;
    }
    default:
      return 'discharge reason';
  }
};

export function offsetDate(date: Date) {
  return dateToMoment(date).toDate();
}

export function toSiteTz(date: Date, siteTimezone: any) {
  return momentToDate(moment.tz(date, moment.tz.guess()).tz(siteTimezone));
}

export const isOverrideOrder = (
  orderType: mumms.EntityOrderType | mumms.DischargeEntityType | null,
) => orderType === OTH || orderType === MMD || orderType === PSO;

/**
 * OP-33240: when picking the location for an order, Patient's Home
 * (facility id -1) is listed first. PIM returns facilities sorted by name.
 */
export const homeFacilityFirst = <T>(facilities: T[]): T[] => {
  const isHome = (facility: T) => Number((facility as any)?.id) === -1;
  return [
    ...facilities.filter((facility) => isHome(facility)),
    ...facilities.filter((facility) => !isHome(facility)),
  ];
};
